import { CopyIcon, ShareIcon, SquarePlusIcon, type LucideIcon } from "lucide-react"
import type { ReactNode } from "react"
import { Button } from "@/primitives/button.tsx"
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/primitives/dialog.tsx"
import { Callout } from "./callout.tsx"
import { copyText } from "./key.tsx"

/*
 * The offer to install Beacon, as it looks: the sheet "Install Beacon" opens where the browser has
 * no prompt of its own, and Home's card. Where and whether each shows is the installed app's
 * (`installed-app/install-offer.tsx`, from `installRoute`). Each says one of two things: how to add
 * Beacon from the Share sheet, or, inside another app's browser (`inAppBrowser`), which can't, to
 * open Beacon in Safari or Chrome first.
 */

/** One of the Share sheet's steps: its glyph on a tile, and what to tap. */
function Step({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
        <Icon aria-hidden className="size-4.5" />
      </span>
      <span className="text-ui">{children}</span>
    </li>
  )
}

/**
 * The sheet with the Share sheet's two steps, or, `inAppBrowser`, the line saying to open Beacon in
 * Safari or Chrome. Either way the page's link is there to copy across: Slack on an iPhone opens
 * links in Safari's own view, which can't be told from Safari, and has no Add to Home Screen.
 */
export function InstallSheet({
  open,
  onOpenChange,
  inAppBrowser,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  inAppBrowser: boolean
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Install Beacon</DialogTitle>
          <DialogDescription>
            {inAppBrowser
              ? "This app's browser can't add Beacon to your home screen. Open Beacon in Safari " +
                "or Chrome, then install it from there."
              : "Beacon opens full screen from your home screen, like any app."}
          </DialogDescription>
        </DialogHeader>
        {!inAppBrowser && (
          <DialogBody className="space-y-4">
            <ol className="space-y-3">
              <Step icon={ShareIcon}>
                Tap <span className="font-medium">Share</span> in the browser&apos;s toolbar
              </Step>
              <Step icon={SquarePlusIcon}>
                Choose <span className="font-medium">Add to Home Screen</span>
              </Step>
            </ol>
            <p className="text-xs text-muted-foreground">
              No Add to Home Screen? You&apos;re in another app&apos;s browser: open Beacon in
              Safari first.
            </p>
          </DialogBody>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => void copyText(window.location.href, "Link copied")}
          >
            <CopyIcon aria-hidden />
            Copy link
          </Button>
          <DialogClose render={<Button />}>Done</DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Home's card offering to put Beacon on the home screen, with Install and Dismiss; `inAppBrowser`,
 * it says to open Beacon in Safari or Chrome to.
 */
export function InstallCallout({
  inAppBrowser,
  onInstall,
  onDismiss,
}: {
  inAppBrowser: boolean
  onInstall: () => void
  onDismiss: () => void
}) {
  return (
    <Callout
      tone="info"
      icon={SquarePlusIcon}
      title="Put Beacon on your home screen"
      action={
        <>
          <Button variant="outline" size="sm" onClick={onInstall}>
            Install
          </Button>
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        </>
      }
    >
      {inAppBrowser
        ? "Open Beacon in Safari or Chrome to add it to your home screen."
        : "It opens full screen, a tap away, like any app."}
    </Callout>
  )
}
